import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function AppleRobotButlerPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Apple ロボットバトラー開発プロジェクト' }
  ];

  const article = {
    category: 'テクノロジー・イノベーション',
    date: '2024年4月4日',
    title: 'Apple ロボットバトラー開発プロジェクト',
    subtitle: 'AI技術が切り開く執事サービスの未来',
    heroImage: '/images/generated/jba-fs-054-news_apple-robot-butler.jpg',
    lead: 'Appleが自律型ロボットバトラーの開発を検討していることが明らかになりました。自動車プロジェクト終了により再配置された人材を活用し、AI技術を駆使した家庭用モバイルロボットの実現を目指しています。この革新的な取り組みは、執事サービスの未来に新たな可能性をもたらすものとして注目されています。',
    sections: [
      {
        heading: 'プロジェクトの背景と経緯',
        image: '/images/generated/jba-fs-055-news_apple-robot-butler-v3.jpg', aspect: 'aspect-[3/4]',
        alt: '先端AIとロボット工学の研究が進む開発ラボのイメージ',
        paragraphs: [
          'Bloombergの報道によると、Appleは2024年2月に自動車プロジェクト「Titan」を正式に中止し、約2,000人の従業員をAI関連プロジェクトに再配置しました。このプロジェクトは少なくとも2014年から存在していましたが、リーダーシップの変更と繰り返しの遅延に悩まされていました。現在、同社は家庭用モバイルロボットへ資源を移行し、自律型ロボットバトラーの開発を検討しています。',
          'この取り組みは、GoogleやMicrosoftなどの競合他社と比較して生成AIで遅れを取っているという批判を受け、Appleがより積極的にAI製品に投資しようとする戦略の一環として位置づけられています。'
        ]
      },
      {
        heading: '想定される機能と技術仕様',
        image: '/images/generated/jba-fs-056-news_apple-robot-butler-v3.jpg', aspect: 'aspect-[3/4]',
        alt: '室内を移動し家事をサポートする家庭用ロボットの機能イメージ',
        paragraphs: [
          '提案されているロボットは、AIモデルを使用して部屋間を移動し、カメラやその他の搭載センサーで周囲の環境を認識する機能を持つことが期待されています。具体的には、ユーザーの後を追って移動する家庭内使用、掃除や食器洗いなどの完全自律的な家事タスク、そしてモバイルビデオ会議ツールとしての機能が想定されています。',
          'ただし、移動方法（ホイール式か二足歩行か）、具体的なタスク実行方法、発売時期、価格、そして実現可能性については、現時点では不明な点が多く残されています。Appleには過去にAirPowerやタブレット型プロトタイプなど、実現しなかったアイデアの歴史もあり、このロボットも同様の可能性があることが指摘されています。'
        ]
      },
      {
        heading: '業界動向と競合状況',
        image: '/images/generated/jba-fs-057-news_apple-robot-butler-v3.jpg', aspect: 'aspect-video',
        alt: '掃除ロボットからヒューマノイドまで多様化する家庭用ロボット業界のイメージ',
        paragraphs: [
          '家庭用ロボット市場では、iRobotがRoomba自律掃除機を世界中で4,000万台以上販売し、AmazonがAstroという家庭監視用ロボットを開発中です。また、Figureは二足歩行のヒューマノイドロボットに大規模言語モデルを統合し、BMWと提携してサウスカロライナの製造施設でタスクを実行しています。',
          '消費者の反応については、Brookings Institution調査（2021年）で成人の61%がロボットに対して不快感を持ち、Pew Research調査（2022年）では成人の37%以上が日常生活でのAI使用の増加について「興奮よりも懸念」を抱いていることが明らかになっています。'
        ]
      }
    ],
    comment: 'Appleによるロボットバトラー開発の取り組みは、執事サービス業界にとって極めて興味深い技術革新の兆候として注目されます。弊協会としても、AI技術の進歩が伝統的な執事サービスに与える影響を慎重に見守っております。ロボット技術は確かに家事の効率化や基本的なサービス提供において有用性を示していますが、真の執事サービスの核心である「おもてなしの心」「状況判断力」「人間的な温かさ」といった要素は、技術では代替できない人間固有の価値であると考えております。私どもとしては、テクノロジーと人間のサービスが相互補完的に発展し、より質の高いホスピタリティの実現に寄与することを期待しており、日本における執事サービスの更なる発展に向けて、このような技術動向も参考にしながら専門性の向上に努めてまいります。',
    source: { name: 'Popular Science', url: 'https://www.popsci.com/technology/apple-home-robot/', access: '2025年10月' },
    related: [
      { to: '/news/downton-abbey-grand-finale', image: '/images/generated/jba-fs-058-news_apple-robot-butler-v3.jpg', aspect: 'aspect-[3/4]', alt: 'Downton Abbey: The Grand Finale 映画公開の関連記事', title: 'Downton Abbey: The Grand Finale 映画公開', desc: '執事文化の集大成として世界的注目' },
      { to: '/news/raffles-butler-campaign', image: '/images/generated/jba-fs-059-news_apple-robot-butler-v3.jpg', aspect: 'aspect-[3/4]', alt: 'Raffles「The Butler Did It」キャンペーンの関連記事', title: 'Raffles「The Butler Did It」キャンペーン', desc: 'ラグジュアリーホテルの執事サービス革新' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'Popular Science', link: false },
    { label: '元記事URL', value: 'https://www.popsci.com/technology/apple-home-robot/', link: true },
    { label: '卓上ロボット報道', value: 'https://www.macrumors.com/2025/08/13/apple-launching-robot-virtual-companion-in-2027/', link: true },
    { label: '発売時期・価格', value: 'https://siliconangle.com/2025/08/13/apple-reportedly-targeting-2027-release-tabletop-robot-movable-display/', link: true },
    { label: 'Amazon Astro', value: 'https://en.wikipedia.org/wiki/Amazon_Astro', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-background', label: 'プロジェクトの背景と経緯' },
    { id: 's-tabletop', label: '報道が伝える「卓上ロボット」の姿' },
    { id: 's-specs', label: '想定される機能と技術仕様' },
    { id: 's-industry', label: '業界動向と競合状況' },
    { id: 's-rivals', label: '競合の家庭用ロボット：Astro と Ballie' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'Appleが自動車プロジェクト終了後の人材を家庭用ロボット開発に再配置していると報じられた',
                '報道では可動ディスプレイ型の「卓上ロボット」を2027年目標で開発中とされ、価格は約1,000ドルの噂',
                'Amazon AstroやSamsung Ballieなど競合も存在するが、家庭用ロボットの商用化は大手でも難路'
              ]}
            />

            <SectionHeading id="s-background" en="Background">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/apple-popsci.jpg"
              alt="AppleのホームロボットについてのPopular Science記事のスクリーンショット"
              media="Popular Science"
              url="https://www.popsci.com/technology/apple-home-robot/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="家事の効率化はできても、「おもてなしの心」「状況判断力」「人間的な温かさ」は技術では代替できない。" cite="全日本執事協会" />
            </div>

            <SectionHeading id="s-tabletop" en="Tabletop Robot">報道が伝える「卓上ロボット」の姿</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Bloombergのマーク・ガーマン記者らの報道によると、開発中とされるのは可動アームの先に約7インチのiPad状ディスプレイを載せた「卓上ロボット」です。ディスプレイは360度回転・傾斜し、FaceTimeの通話中には自動で話者を追尾、スマートホームの操作やホームセキュリティの監視も担うAIコンパニオンとして位置づけられています。</p>
              <p className={P}>報道が伝える想定発売時期は<Stat>2027年</Stat>を目標とし、価格は約<Stat>1,000ドル</Stat>前後への引き下げを目指すとされます。社内コードネームは「J595」で、2022年に承認された後に開発が加速したと報じられています（いずれも報道時点の観測で、確定情報ではありません）。</p>
            </div>
            <SourceShot
              src="/images/news-sources/apple-wikipedia.jpg"
              alt="Apple社の企業概要を解説するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（Apple Inc.）"
              url="https://en.wikipedia.org/wiki/Apple_Inc."
            />

            <SectionHeading id="s-specs" en="Specifications">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-industry" en="Industry">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-rivals" en="Rivals">競合の家庭用ロボット：Astro と Ballie</SectionHeading>
            <div className="space-y-6">
              <p className={P}>家庭用ロボットの実製品としては、Amazonの「Astro」が代表例です。Alexa対応で家庭内を自走し、監視カメラや荷物運びを担うもので、2021年に発表され、招待制で価格は<Stat>約1,599ドル</Stat>（当初999ドルから上昇）とされています。</p>
              <p className={P}>一方、Samsungが繰り返し発表してきた球体型AIロボット「Ballie」は、2025年夏の発売目標を達成できず、2026年時点でも出荷に至っていません。家庭用ロボットは大手でも商用化のハードルが高く、Appleの計画も同様の不確実性をはらんでいます。</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">報道</dt><dd className="text-[#333] mt-0.5">Bloomberg（Mark Gurman）</dd></div>
                  <div><dt className="text-[#8f7a56]">想定発売</dt><dd className="text-[#333] mt-0.5">2027年目標（報道）</dd></div>
                  <div><dt className="text-[#8f7a56]">想定価格</dt><dd className="text-[#333] mt-0.5">約1,000ドル（噂）</dd></div>
                  <div><dt className="text-[#8f7a56]">コードネーム</dt><dd className="text-[#333] mt-0.5">J595</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
